import React, { useState, useEffect } from 'react';

import TextInputWithLabel from '../components/Inputs/TextInput.jsx';
import SelectWithLabel from '../components/Inputs/Select.jsx';
import { MobileView, BrowserView } from 'react-device-detect';
import Card from '../components/Ui/Card.jsx';
import ImageUploader from '../components/Ui/ProfileImage.jsx';
import { fetchAccount, updateAccount, resetPassword, createTheme, updateTheme, fetchTitles } from '../http/account.js';
import { Alert } from '../components/Ui/Alert.jsx';
import Modal from '../components/Ui/Modal.jsx';
import { CancelButton, SuccessButton, DefaultButton } from '../components/StyledComponents/Buttons/Buttons.jsx';
import { StyledModal, ModalHeader, ModalBody, ModalFooter } from '../components/StyledComponents/Modals/Modals.jsx';
import { FieldContainer, Row, CenterContainer, FormContainer, ImageContainer, LeftContainer } from '../components/StyledComponents/Forms/Forms.jsx';

export default function Account({ email, colour, handleImageUpload }) {
  const [titles, setTitles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [account, setAccount] = useState({});
  const [originalAccount, setOriginalAccount] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  const [missingFields, setMissingFields] = useState({});
  const [missingPasswordFields, setMissingPasswordFields] = useState({});
  const [invalidData, setInvalidData] = useState({ email: '', phone: '' });
  const [passwords, setPasswords] = useState({ password1: '', password2: '' });
  const [passwordErrors, setPasswordErrors] = useState();

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?(\d.*){3,}$/;

  useEffect(() => {
    const loadAccount = async () => {
      setIsLoading(true);
      try {
        const fetchedAccount = await fetchAccount(email);
        setOriginalAccount(fetchedAccount);
        setAccount(fetchedAccount.Users_Profile);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    const loadTitles = async () => {
      setIsLoading(true);
      try {
        const titles = await fetchTitles();

        const restructuredTitles = [{ label: 'Select a title..', value: '' }];

        restructuredTitles.push(...titles.map(title => ({
          label: title.title,
          value: title.title
        })));

        setTitles(restructuredTitles);
        setError(null);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccount();
    loadTitles();
  }, [email]);

  const handleChange = (field, value) => {
    setAccount(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordInput = (field, value) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailPhone = (field, value) => {
    setInvalidData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    const isValid = validateForm();
    if (!isValid) {
      setSuccess(false);
      return;
    }

    setIsLoading(true);
    try {
      originalAccount.Users_Profile = account;

      await updateAccount(originalAccount);

      if (colour.isDefault) {
        await createTheme(colour, account.unqID);
        colour.isDefault = false;
      } else {
        await updateTheme(colour, account.unqID);
      }
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
      setSuccess(true);
    }
  };

  const validateForm = () => {
    let newMissingFields = {};
    let isValid = true;
    handleEmailPhone('email', '');
    handleEmailPhone('phone', '');

    if (!account.title) newMissingFields.title = true;
    if (!account.christname) newMissingFields.christname = true;
    if (!account.surname) newMissingFields.surname = true;

    if (!account.eaddress) {
      newMissingFields.eaddress = true;
    } else if (!emailRegex.test(account.eaddress)) {
      handleEmailPhone('email', "Invalid email address");
      isValid = false;
    }

    if (!account.cotelephone) {
      newMissingFields.cotelephone = true;
    } else if (!phoneRegex.test(account.cotelephone)) {
      handleEmailPhone('phone', "Invalid phone number")
      isValid = false;
    }

    if (!account.companyname) newMissingFields.companyname = true;
    if (!account.jobtitle) newMissingFields.jobtitle = true;
    if (!account.coaddress1) newMissingFields.coaddress1 = true;
    if (!account.copostcode) newMissingFields.copostcode = true;

    for (let key in newMissingFields) {
      if (newMissingFields[key] === true) {
        isValid = false;
        break;
      }
    }

    setMissingFields(newMissingFields);
    return isValid;
  };

  const handleUpdatePassword = async () => {
    const isValid = validatePassword();

    if (!isValid || (account?.unqID === null || account?.eaddress === null)) {
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(account?.unqID, account?.eaddress, passwords.password1);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setIsLoading(false);
      setPasswordErrors();
      setPasswordSuccess(true);
    }
  };

  const validatePassword = () => {
    setPasswordErrors();
    let newMissingFields = {};
    let isValid = true;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

    if (!passwords.password1) newMissingFields.password1 = true;
    if (!passwords.password2) newMissingFields.password2 = true;

    for (let key in newMissingFields) {
      if (newMissingFields[key] === true) {
        isValid = false;
        setPasswordErrors("Both fields must be populated");
        break;
      }
    }

    if (isValid) {
      if (passwords.password1 !== passwords.password2) {
        isValid = false;
        setPasswordErrors("Passwords do not match");
      }
      else {
        if (!passwordRegex.test(passwords.password1)) {
          isValid = false;
          setPasswordErrors("Password must be at least 8 characters, include uppercase, lowercase, number, and symbol");
        }
      }
    }

    setMissingPasswordFields(newMissingFields);
    return isValid;
  }

  return (
    <>
      <Modal open={isModalOpen}>
        <StyledModal>
          <ModalHeader>Reset Password</ModalHeader>
          <ModalBody>
            <TextInputWithLabel type='password' missing={missingPasswordFields.password1} label='Password' required={true} onChange={e => handlePasswordInput('password1', e.target.value)} />
            <TextInputWithLabel type='password' missing={missingPasswordFields.password2} label='Confirm Password' required={true} onChange={e => handlePasswordInput('password2', e.target.value)} />
          </ModalBody>
          <ModalFooter>
            {passwordErrors && <Alert type='warning'>{passwordErrors}</Alert>}
            {passwordSuccess && <Alert type='success'>Password updated successfully</Alert>}
            <CancelButton onClick={() => setIsModalOpen(false)}>Cancel</CancelButton>
            <SuccessButton
              disabled={isLoading}
              onClick={() => {
                handleUpdatePassword();
              }}>Change Password</SuccessButton>
          </ModalFooter>
        </StyledModal>
      </Modal>
      <CenterContainer>
        <FormContainer>
          <Card label={'Account Details'} headercolour={colour.sidebar} headerTextColour={colour.font}>
            {isLoading && <p>Loading...</p>}
            {error && !isLoading && <Alert type="error">Unable to process request due to <b>{error.message}</b></Alert>}
            {!isLoading && !error && (
              <>
                <Row>
                  <LeftContainer>
                    <MobileView>
                      <ImageContainer>
                        <ImageUploader imageSrc={colour.image ? colour.image : ''} onImageUpload={handleImageUpload} />
                      </ImageContainer>
                    </MobileView>
                    <p>Personal Details</p>
                    <FieldContainer>
                      <SelectWithLabel
                        label="Title"
                        options={titles}
                        missing={missingFields.title}
                        value={account.title}
                        onChange={e => handleChange('title', e.target.value)} />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel label='First name' missing={missingFields.christname} value={account.christname} onChange={e => handleChange('christname', e.target.value)} />
                    </FieldContainer>
                    <FieldContainer>
                      <TextInputWithLabel label='Surname' missing={missingFields.surname} value={account.surname} onChange={e => handleChange('surname', e.target.value)} />
                    </FieldContainer>
                  </LeftContainer>

                  <BrowserView>
                    <ImageContainer>
                      <ImageUploader imageSrc={colour.image} onImageUpload={handleImageUpload} />
                    </ImageContainer>
                  </BrowserView>
                </Row>

                <p>Login</p>
                <Row>
                  <FieldContainer>
                    <TextInputWithLabel label='Username' missing={missingFields.eaddress} value={account.eaddress} onChange={e => handleChange('eaddress', e.target.value)} disabled={true} />
                  </FieldContainer>
                  <FieldContainer>
                    <TextInputWithLabel label='Password' type='password' disabled={true} value='password' />
                    <a href='#' onClick={() => setIsModalOpen(true)} style={{ fontSize: '12px', position: 'absolute', marginTop: '-10px', textDecoration: 'none' }}>Reset Password</a>
                  </FieldContainer>
                </Row>

                <p>Contact</p>
                <TextInputWithLabel label='Email' invalidData={invalidData.email} missing={missingFields.eaddress} value={account.eaddress} onChange={e => handleChange('eaddress', e.target.value)} />
                <TextInputWithLabel label='Telephone' invalidData={invalidData.phone} missing={missingFields.cotelephone} value={account.cotelephone} onChange={e => handleChange('cotelephone', e.target.value)} />

                <Row>
                  <FieldContainer>
                    <TextInputWithLabel label='Company' missing={missingFields.companyname} value={account.companyname} onChange={e => handleChange('companyname', e.target.value)} />
                  </FieldContainer>
                  <FieldContainer>
                    <TextInputWithLabel label='Job Title' missing={missingFields.jobtitle} value={account.jobtitle} onChange={e => handleChange('jobtitle', e.target.value)} />
                  </FieldContainer>
                </Row>

                <p>Address</p>
                <FieldContainer>
                  <TextInputWithLabel label='Address 1' missing={missingFields.coaddress1} value={account.coaddress1} onChange={e => handleChange('coaddress1', e.target.value)} />
                </FieldContainer>
                <FieldContainer>
                  <TextInputWithLabel label='Address 2' optional={true} value={account.coaddress2} onChange={e => handleChange('coaddress2', e.target.value)} />
                </FieldContainer>
                <FieldContainer>
                  <TextInputWithLabel label='Address 3' optional={true} value={account.coaddress3} onChange={e => handleChange('coaddress3', e.target.value)} />
                </FieldContainer>

                <Row>
                  <FieldContainer>
                    <TextInputWithLabel label='County' value={account.coaddress4} onChange={e => handleChange('coaddress4', e.target.value)} />
                  </FieldContainer>
                  <FieldContainer>
                    <TextInputWithLabel label='Postcode' missing={missingFields.copostcode} value={account.copostcode} onChange={e => handleChange('copostcode', e.target.value)} />
                  </FieldContainer>
                </Row>
                {Object.values(missingFields).some(isMissing => isMissing) &&
                  <Alert type='warning'>Please populate all required fields</Alert>
                }
                {success && <Alert type='success'>Account updated successfully</Alert>}
                <DefaultButton
                  onClick={handleSaveChanges}
                  disabled={isLoading}>Save Changes</DefaultButton>
              </>
            )}
          </Card>
        </FormContainer>
      </CenterContainer>
    </>
  );
}